/**
 * DDPreviewCard - Card component for displaying a single design direction
 *
 * Shows thumbnail preview, status badge, variant name, estimated leads, and action buttons
 * Uses TemplatePreview for creatomate templates (same as main campaign page)
 */

import { FC, memo, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Text,
  Badge,
  Flex,
  IconButton,
  Skeleton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Textarea,
  Spinner,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import TemplatePreview from 'src/components/templates/TemplatePreview';
import LoadingList from 'src/components/campaigns/design-directions/LoadingList';

export interface DesignDirection {
  id: string;
  variant: string;
  status: string;
  locked?: boolean;
  adEngine?: 'creatomate' | 'photopea';
  estimatedLeads?: number;
  attributes?: {
    elements?: Array<any>;
    width?: number;
    height?: number;
    templateId?: string;
    image?: {
      flatFile?: string;
    };
    [key: string]: unknown;
  };
}

interface DDPreviewCardProps {
  designDirection: DesignDirection;
  campaignId: string;
  isSelected?: boolean;
  onSelect?: () => void;
  onRegenerate?: (feedback?: string) => void;
}

// Refresh icon
const RefreshIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1 4V10H7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M23 20V14H17"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Edit/Pencil icon
const EditIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Zoom/Expand icon
const ZoomIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15 3H21V9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 21H3V15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 3L14 10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 21L10 14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Close icon
const CloseIcon: FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Check icon for selection
const CheckIcon: FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20 6L9 17L4 12"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Leads icon
const LeadsIcon: FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DDPreviewCard: FC<DDPreviewCardProps> = memo(
  ({ designDirection, campaignId, isSelected = false, onSelect, onRegenerate }) => {
    const navigate = useNavigate();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [regenerateFeedback, setRegenerateFeedback] = useState('');
    const { isOpen: isRegenerateModalOpen, onOpen: openRegenerateModal, onClose: closeRegenerateModal } = useDisclosure();
    const { isOpen: isZoomModalOpen, onOpen: openZoomModal, onClose: closeZoomModal } = useDisclosure();

    const { id, variant, status, locked, adEngine, attributes, estimatedLeads } = designDirection;

    const isGenerating = status !== 'GENERATED';
    const isLocked = locked === true;
    const isCreatomate = adEngine === 'creatomate' && attributes?.elements;

    // Normalize template for TemplatePreview (same as DDImage does)
    const normalizedTemplate = useMemo(() => {
      if (isCreatomate && attributes) {
        const clone = JSON.parse(JSON.stringify(attributes));
        if (Array.isArray(clone.elements)) {
          clone.elements = clone.elements.map((el: any) => {
            if (typeof el.x === 'string') el.x = el.x.trim();
            if (typeof el.y === 'string') el.y = el.y.trim();
            if (typeof el.width === 'string') el.width = el.width.trim();
            if (typeof el.height === 'string') el.height = el.height.trim();
            if (el.elements && Array.isArray(el.elements)) {
              el.elements = el.elements.map((subEl: any) => {
                if (typeof subEl.x === 'string') subEl.x = subEl.x.trim();
                if (typeof subEl.y === 'string') subEl.y = subEl.y.trim();
                if (typeof subEl.width === 'string') subEl.width = subEl.width.trim();
                if (typeof subEl.height === 'string') subEl.height = subEl.height.trim();
                return subEl;
              });
            }
            return el;
          });
        }
        return clone;
      }
      return null;
    }, [isCreatomate, attributes]);

    const handleSelect = useCallback(() => {
      if (onSelect && !isGenerating) {
        onSelect();
      }
    }, [onSelect, isGenerating]);

    const handleRegenerateClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      openRegenerateModal();
    }, [openRegenerateModal]);

    const handleRegenerateConfirm = useCallback(() => {
      if (onRegenerate) {
        onRegenerate(regenerateFeedback || undefined);
      }
      setRegenerateFeedback('');
      closeRegenerateModal();
    }, [onRegenerate, regenerateFeedback, closeRegenerateModal]);

    const handleZoomClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      openZoomModal();
    }, [openZoomModal]);

    const handleEditClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      // Navigate to campaign page in editing mode with DD reference and returnTo flag
      navigate(`/projects/campaigns/${campaignId}/editing?dd=${id}&returnTo=chat`);
    }, [navigate, campaignId, id]);

    const getStatusBadge = () => {
      // Only show badge when generating, not when ready
      if (isGenerating) {
        return (
          <Badge colorScheme="orange" fontSize="xs" px={2} py={0.5} borderRadius="full">
            Generating...
          </Badge>
        );
      }
      return null;
    };

    const renderPreview = (size: 'small' | 'large' = 'small') => {
      if (isGenerating) {
        return (
          <Box w="100%" h="100%" position="relative">
            <LoadingList
              isPendingGeneration
              text1="Building layout"
              text2="Generating ad"
              text3="Splitting in multiple layers"
              height="100%"
              width="100%"
              size={size === 'large' ? 'md' : 'sm'}
              borderRadius="0"
            />
          </Box>
        );
      }

      if (normalizedTemplate) {
        return (
          <Box w="100%" h="100%" pointerEvents="none" overflow="hidden">
            <TemplatePreview
              template={normalizedTemplate}
              onReady={() => setImageLoaded(true)}
            />
          </Box>
        );
      }

      // Fallback for non-creatomate or missing attributes
      return (
        <Flex
          w="100%"
          h="100%"
          align="center"
          justify="center"
          bg="gray.200"
          color="gray.500"
        >
          <Text fontSize="sm">No preview</Text>
        </Flex>
      );
    };

    // Format estimated leads as multiplier (e.g., "1.25x")
    const formatLeads = (leads?: number) => {
      if (!leads) return null;
      // Value is expected to be between 1 and 2 (e.g., 1.25, 1.5, etc.)
      return `${leads.toFixed(2).replace(/\.?0+$/, '')}x`;
    };

    return (
      <>
        <Box
          borderRadius="lg"
          overflow="hidden"
          bg="white"
          border="2px solid"
          borderColor={isSelected ? '#F7480B' : 'gray.200'}
          boxShadow={isSelected ? 'md' : 'sm'}
          transition="all 0.2s ease-in-out"
          cursor={isGenerating ? 'default' : 'pointer'}
          opacity={isGenerating ? 0.7 : 1}
          _hover={!isGenerating ? { boxShadow: 'md', borderColor: isSelected ? '#F7480B' : 'gray.300' } : {}}
          onClick={handleSelect}
          position="relative"
        >
          {/* Selection indicator */}
          {isSelected && (
            <Box
              position="absolute"
              top={2}
              left={2}
              w="28px"
              h="28px"
              bg="#F7480B"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={2}
              color="white"
            >
              <CheckIcon />
            </Box>
          )}

          {/* Preview area - Square and larger */}
          <Box position="relative" w="100%" pb="100%" bg="gray.100">
            <Box position="absolute" top={0} left={0} right={0} bottom={0}>
              {!imageLoaded && !isGenerating && normalizedTemplate && (
                <Skeleton position="absolute" top={0} left={0} w="100%" h="100%" />
              )}
              {renderPreview('small')}
            </Box>

            {/* Status badge overlay */}
            <Box position="absolute" top={2} right={2} zIndex={1}>
              {getStatusBadge()}
            </Box>

            {/* Zoom button overlay */}
            {!isGenerating && (
              <Box position="absolute" bottom={2} right={2} zIndex={1}>
                <Tooltip label="View larger" placement="top">
                  <IconButton
                    aria-label="View design larger"
                    icon={<ZoomIcon />}
                    size="sm"
                    bg="white"
                    boxShadow="sm"
                    _hover={{ bg: 'gray.100' }}
                    onClick={handleZoomClick}
                  />
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Card footer */}
          <Flex
            p={3}
            align="center"
            justify="space-between"
            borderTop="1px solid"
            borderColor="gray.100"
          >
            <Box>
              <Text fontWeight="medium" fontSize="sm" color="gray.800">
                Design {variant}
              </Text>
              {/* Only show estimated leads when DD is fully generated */}
              {!isGenerating && estimatedLeads && (
                <Flex align="center" gap={1} mt={0.5} flexWrap="wrap">
                  <LeadsIcon />
                  <Text fontSize="xs" color="gray.500">
                    {formatLeads(estimatedLeads)} leads
                  </Text>
                  {/* Show "Recommended" badge for variant A */}
                  {variant === 'A' && (
                    <Badge
                      colorScheme="green"
                      fontSize="9px"
                      px={1.5}
                      py={0.5}
                      borderRadius="full"
                      ml={1}
                    >
                      Recommended
                    </Badge>
                  )}
                </Flex>
              )}
            </Box>

            {/* Edit and Regenerate buttons hidden but logic preserved for future use
            <Flex gap={1}>
              {!isLocked && !isGenerating && (
                <>
                  <Tooltip label="Edit design" placement="top">
                    <IconButton
                      aria-label="Edit design direction"
                      icon={<EditIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                      onClick={handleEditClick}
                    />
                  </Tooltip>
                  <Tooltip label="Regenerate" placement="top">
                    <IconButton
                      aria-label="Regenerate design direction"
                      icon={<RefreshIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="gray"
                      onClick={handleRegenerateClick}
                    />
                  </Tooltip>
                </>
              )}
            </Flex>
            */}
          </Flex>
        </Box>

        {/* Regenerate Modal - Higher z-index to appear above chat panel */}
        <Modal isOpen={isRegenerateModalOpen} onClose={closeRegenerateModal} isCentered>
          <ModalOverlay zIndex={20000} />
          <ModalContent containerProps={{ zIndex: 20001 }}>
            <ModalHeader>Regenerate Design {variant}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={3} color="gray.600" fontSize="sm">
                Optionally provide feedback to guide the regeneration:
              </Text>
              <Textarea
                placeholder="e.g., Make it more colorful, use different fonts, add more contrast..."
                value={regenerateFeedback}
                onChange={(e) => setRegenerateFeedback(e.target.value)}
                rows={3}
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={closeRegenerateModal}>
                Cancel
              </Button>
              <Button colorScheme="orange" onClick={handleRegenerateConfirm}>
                Regenerate
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Zoom/Preview Modal - Higher z-index to appear above chat panel */}
        <Modal isOpen={isZoomModalOpen} onClose={closeZoomModal} isCentered size="lg">
          <ModalOverlay bg="blackAlpha.800" zIndex={20000} />
          <ModalContent containerProps={{ zIndex: 20001 }} bg="white" borderRadius="lg" maxW="450px" overflow="hidden">
            {/* Close button */}
            <IconButton
              aria-label="Close preview"
              icon={<CloseIcon />}
              position="absolute"
              top={2}
              right={2}
              zIndex={10}
              size="sm"
              bg="blackAlpha.600"
              color="white"
              borderRadius="full"
              _hover={{ bg: 'blackAlpha.800' }}
              onClick={closeZoomModal}
            />

            {/* Preview content */}
            <Box w="100%" pb="100%" position="relative" bg="white">
              <Box position="absolute" top={0} left={0} right={0} bottom={0}>
                {renderPreview('large')}
              </Box>
            </Box>

            {/* Footer info */}
            <Box p={3} borderTop="1px solid" borderColor="gray.100" textAlign="center">
              <Text fontWeight="medium" fontSize="md" color="gray.800">
                Design {variant}
              </Text>
              {!isGenerating && estimatedLeads && (
                <Flex align="center" justify="center" gap={1} mt={1}>
                  <LeadsIcon />
                  <Text fontSize="sm" color="gray.500">
                    {formatLeads(estimatedLeads)} leads
                  </Text>
                </Flex>
              )}
            </Box>
          </ModalContent>
        </Modal>
      </>
    );
  }
);

DDPreviewCard.displayName = 'DDPreviewCard';

export default DDPreviewCard;
